import dynamic from "next/dynamic";
import Head from "next/head";
import { useRouter } from "next/router";
import useSWRInfinite from "swr/infinite";

import { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import InfiniteScroll from "react-swr-infinite-scroll";

import truncate from "lodash/truncate";
import { twMerge } from "tailwind-merge";
import { useToggle } from "usehooks-ts";

import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";

import AnnoucementSnippet from "components/AnnouncementSnippet";
import { Loader } from "components/customElements/loader";
import EmptyState from "components/EmptyState";
import { entityUrl } from "components/EntityPageLayout";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import { useApi } from "hooks/useApi";
import { ApiEndpoint, baseFetcher } from "utils/baseFetcher";
import shortSecurity from "utils/shortSecurity";

import styles from "./style.module.scss";

const FilingModal = dynamic(() => import("components/FilingModal"));

type EntityPageFilingsListProps = {};

export const entityFilingsUrl = (
  slug: string,
  pageIndex: number,
  filterTxt?: string,
  options?: { isFocused?: boolean }
) => {
  const url = new ApiEndpoint(
    `/api/v2/entities/${slug}/exchange-announcements`
  );
  url.searchParams.append(
    "include",
    "entity,locker-items,corporate-attachments"
  );
  url.searchParams.append("page[size]", "5");
  url.searchParams.append("page[number]", `${pageIndex}`);
  url.searchParams.append("sort", "-release-time");

  if (filterTxt) {
    url.searchParams.append("filter[fulltext]", filterTxt);
  }

  if (options?.isFocused) {
    url.searchParams.append("filter[high-priority]", "true");
  }

  return url.href;
};

const EntityPageFilingsList = ({}: EntityPageFilingsListProps) => {
  const router = useRouter();
  const slug = router.query.slug as string;
  const itemId = router.query.item as string;

  const [filterTxt, setFilterTxt] = useState("");
  const [filterTxtValue, setFilterTxtValue] = useState("");
  const [filterTxtError, setFilterTxtError] = useState(
    "Please enter at least 3 characters"
  );
  useEffect(() => {
    if (filterTxtValue?.length >= 3) {
      setFilterTxt(filterTxtValue);
      setFilterTxtError("");
    } else {
      setFilterTxt("");
      setFilterTxtError("Please enter at least 3 characters");
    }
  }, [filterTxtValue]);

  const [isFocused, toggleFocused] = useToggle(false);

  const { data, mutate, isValidating, size, setSize } = useSWRInfinite(
    (pageIndex, previousPageData) => {
      if (previousPageData?.data?.length === 0) return null;

      return entityFilingsUrl(slug, pageIndex + 1, filterTxt, { isFocused });
    },
    baseFetcher,
    {
      revalidateFirstPage: false,
      revalidateOnMount: false,
      revalidateOnFocus: false,
    }
  );

  const hasMore = data?.[size - 1]?.meta?.totalPages > size;
  const isEmpty = !data?.[0]?.data?.length;

  useEffect(() => {
    if (!hasMore) mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const firstFiling = data?.[0]?.data?.[0];
  const { data: entity } = useApi(entityUrl(slug));
  const { prettyName: name, security } = entity || {};
  const shortedSecurity = shortSecurity(security);
  const pageTitleName = useMemo(() => {
    if (shortedSecurity) {
      return `${name} | ${shortedSecurity} `;
    }
    return name;
  }, [shortedSecurity, name]);
  const title = `${pageTitleName} - Announcements - Filings`;
  const description = truncate(
    `Latest Exchange Filings on ${name} (${entity?.uniqueTicker})${
      firstFiling ? ` - ${firstFiling.title}` : ""
    }`,
    {
      length: 155,
      separator: /[/\- ]/,
    }
  );

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <div className={styles.container}>
        <div
          className={twMerge(
            styles.filter,
            "px-[15px] pb-[15px] md:px-[30px] md:py-[15px] md:flex md:items-center"
          )}
        >
          <span
            className={twMerge(styles.refresh, "hidden md:block")}
            onClick={() => mutate()}
          >
            Refresh
          </span>
          <div
            className={twMerge(
              styles.filterBox,
              "ml-0 md:ml-[30px] py-[5px] mr-[10px] w-full"
            )}
          >
            <FontAwesomeIcon icon={faSearch} />
            <OverlayTrigger
              placement="bottom"
              overlay={
                filterTxtError ? <Tooltip>{filterTxtError}</Tooltip> : <></>
              }
            >
              <input
                value={filterTxtValue}
                autoComplete="false"
                autoCapitalize="false"
                onChange={(e) => setFilterTxtValue(e.target.value)}
                aria-label="search"
                placeholder="Search"
                className="w-full md:max-w-[240px]"
              />
            </OverlayTrigger>
          </div>
        </div>
        <div className={styles.list}>
          <InfiniteScroll
            swr={{ data, mutate, isValidating, size, setSize }}
            isReachingEnd={!hasMore}
            endingIndicator={
              isValidating ? (
                <Loader numberOfItems={5} />
              ) : isEmpty ? (
                <EmptyState
                  type={filterTxt ? "search" : "filing"}
                  query={filterTxt}
                  onClearClick={() => setFilterTxtValue("")}
                  entity={entity}
                />
              ) : (
                <div className={styles.endOfList}>No more filings</div>
              )
            }
          >
            {(pageData: any) => {
              if (!pageData) return null;

              return pageData?.data?.map?.((filing: any) => (
                <AnnoucementSnippet key={filing.id} item={filing} />
              ));
            }}
          </InfiniteScroll>
        </div>
      </div>
      {itemId && <FilingModal id={itemId} />}
    </>
  );
};

export default EntityPageFilingsList;
